import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";
import { BehaviorSubject, combineLatest, map, Observable, of, shareReplay, tap, catchError} from "rxjs";

@Injectable({
  providedIn: "root",
})
export class CommonService {
  private apiUrl = environment.apiEndpoint;
  private roleUrl = environment.roleEndPoint;
  constructor(private http: HttpClient) { }

  private workTypesCache: any;
  private objectLevelsCache: any;
  private allCausesCache: any;
  private failureMechanismsCache: any;
  private allMaintainableItemsCache: any;
  private allPerformedWorkAICache: any;

  private userFeaturePermissions = new BehaviorSubject<any>(null);
  private userRole = new BehaviorSubject<any>(null);
  private isRoleLoading = new BehaviorSubject<boolean>(false);

  userFeaturePermissions$ = this.userFeaturePermissions.asObservable();
  userRole$ = this.userRole.asObservable();
  isRoleLoading$ = this.isRoleLoading.asObservable();
  isTerminalUser$: Observable<boolean> = this.userRole$.pipe(
    /* Avoid logging user-sensitive information to the console.
     tap((role) => console.log("User role in isTerminalUser$:", role)),
     */
    // Map the role to a boolean indicating if the user is a terminal user
    map((role) => {
      const terminalUsers = [
        "RepairTechnician",
        "RepairManager",
        "RepairPlanner",
      ];
      return terminalUsers.includes(role);
    })
  );

  isRAMUser$: Observable<boolean> = this.userRole$.pipe(
    /* Avoid logging user-sensitive information to the console.
     tap((role) => console.log("User role in isRAMUser$:", role)),
     */
    // Map the role to a boolean indicating if the user is a RAM user
    map((role) => {
      const ramUsers = [
        "RAMLead",
        "RAMEngineer",
        "CoEReliability",
      ];
      return ramUsers.includes(role);
    })
  );

  isRAMAdminUser$: Observable<boolean> = this.userRole$.pipe(
    /* Avoid logging user-sensitive information to the console.
     tap((role) => console.log("User role in isRAMAdminUser$:", role)),
      */
    // Map the role to a boolean indicating if the user is a RAM admin user
    map((role) => {
      const ramAdminUsers = [
        "RAMAdmin",
      ];
      return ramAdminUsers.includes(role);
    })
  );

  isCoEReliabilityUser$: Observable<boolean> = this.userRole$.pipe(
    /* Avoid logging user-sensitive information to the console.
     tap((role) => console.log("User role in isCoEReliabilityUser$:", role)),
      */
    // Map the role to a boolean indicating if the user is a CoE Reliability user
    map((role) => {
      const coEReliabilityUsers = [
        "CoEReliability",
      ];
      return coEReliabilityUsers.includes(role);
    })
  );

  // Combined observable
  combined$ = combineLatest([this.userFeaturePermissions$, this.userRole$]);

  private terminalCache = new Map<string, Observable<any>>();

  setuserFeaturePermissions(data: any) {
    this.userFeaturePermissions.next(data);
  }

  toggleRoleLoading(isLoading: boolean) {
    this.isRoleLoading.next(isLoading);
  }

  setUserRole(role: any) {
    this.userRole.next(role);
  }

  fetchRegions() {
    return this.http.get(`${this.apiUrl}/Filter/regions`);
  }

  fetchPortfolios() {
    return this.http.get(`${this.apiUrl}/Filter/portfolios`);
  }

  fetchTerminalsByRegion(region: any) {
    let params = new HttpParams();
    if (region) {
      params = params.set("region", region);
    }
    return this.http.get(`${this.apiUrl}/TerminalManagement/getallterminals`, {
      params,
    });
  }

  getTerminalsByRegion(region: any) {
    let params = new HttpParams();
    if (region) {
      params = params.set("region", region);
    }
    return this.http.get(`${this.apiUrl}/Filter/getterminals`, {
      params,
    });
  }

  getTerminalsByPortfolio(portfolio: any) {
    if (!this.terminalCache.has(portfolio)) {

      let params = new HttpParams();

      if (portfolio) {
        params = params.set('portfolio', portfolio);
      }

      const request$ = this.http
        .get(`${this.apiUrl}/Filter/terminalsbyportfolio`, { params })
        .pipe(
          catchError((error) => {
            console.error('Error fetching terminals:', error);

            // Remove failed request from cache so future calls can retry
            this.terminalCache.delete(portfolio);

            // Return empty successful response
            return of({
              success: true,
              data: []
            });
          }),
          shareReplay(1)
        );

      this.terminalCache.set(portfolio, request$);
    }

    return this.terminalCache.get(portfolio)!;
  }

  fetchTerminals() {
    return this.http.get(`${this.apiUrl}/filter/getterminals`);
  }

  fetchAllRegions() {
    return this.http.get(`${this.apiUrl}/Filter/regions`);
  }

  fetchObjectTypes(terminals = "") {
    return this.http.get(
      `${this.apiUrl}/Filter/getobjecttypes?terminalCode=${terminals}`
    );
  }

  fetchObjectIds(terminals = "", objectTypes = "") {
    return this.http.get(
      `${this.apiUrl}/Filter/getobjectids?terminalCode=${terminals}&objectType=${objectTypes}`
    );
  }

  fetchAssetTypes(terminalCode: string) {
    return this.http.get(
      `${this.apiUrl}/filter/getobjecttypes?terminalCode=${terminalCode}`
    );
  }

  // fetchWorkTypes() {
  //   return this.http.get(`${this.apiUrl}/filter/getworktypes`);
  // }

  // fetchObjectLevels() {
  //   return this.http.get(`${this.apiUrl}/filter/getobjectlevels`);
  // }

  // fetchAllCauses() {
  //   return this.http.get(`${this.apiUrl}/filter/getallcauses`);
  // }

  // fetchFailureMechanisms() {
  //   return this.http.get(`${this.apiUrl}/filter/getallfailuremechanisms`);
  // }

  // fetchAllMaintainableItems() {
  //   return this.http.get(`${this.apiUrl}/filter/getallmaintainableitems`);
  // }

  fetchAllWorkTypes() {
    return this.http.get(`${this.apiUrl}/filter/getworktypes`);
  }

  fetchObjectIDsFromAPI(filters: any, filter: any) {
    let objectTDsUrl = "assets/mock-data/object-ids-mockData.json";
    return this.http.get<any>(objectTDsUrl);
  }

  fetchMultilevelObjectIDs(objectTypes: any, objectLevels: any) {
    return this.http.get(
      `${this.apiUrl}/Filter/getobjectids?objectTypes=${objectTypes}&objectLevels=${objectLevels}`
    );
  }

  fetchRoles(team?: any) {
    let name = "badactor";
    let url = `${this.roleUrl}/user/get-roles?applicationname=${name}`;
    if (team) {
      url += `&rolename=${team}`;
    }
    return this.http.get(url);
  }

  getUsers() {
    let appname = "badactor";
    return this.http.get(
      `${this.roleUrl}/user/get-user-list-by-terminal?&applicationname=${appname}`
    );
  }

  getUsersByRole(role: any, terminal: any) {
    let appname = "badactor";
    return this.http.get(
      `${this.roleUrl}/user/get-user-list-by-terminal-and-role?&applicationname=${appname}&terminals=${terminal}&roleName=${role}`
    );
  }

  fetchWorkTypes(forceRefresh: boolean = false): Observable<any> {
    if (this.workTypesCache && !forceRefresh) {
      return of(this.workTypesCache);
    }
    return this.http
      .get(`${this.apiUrl}/filter/getworktypes`)
      .pipe(tap((data: any) => (this.workTypesCache = data)));
  }

  fetchObjectLevels(forceRefresh: boolean = false): Observable<any> {
    if (this.objectLevelsCache && !forceRefresh) {
      return of(this.objectLevelsCache);
    }
    return this.http
      .get(`${this.apiUrl}/filter/getobjectlevels`)
      .pipe(tap((data) => (this.objectLevelsCache = data)));
  }

  fetchAllCauses(forceRefresh: boolean = false): Observable<any> {
    if (this.allCausesCache && !forceRefresh) {
      return of(this.allCausesCache);
    }
    return this.http
      .get(`${this.apiUrl}/filter/getallcauses`)
      .pipe(tap((data) => (this.allCausesCache = data)));
  }

  fetchFailureMechanisms(forceRefresh: boolean = false): Observable<any> {
    if (this.failureMechanismsCache && !forceRefresh) {
      return of(this.failureMechanismsCache);
    }
    return this.http
      .get(`${this.apiUrl}/filter/getallfailuremechanisms`)
      .pipe(tap((data) => (this.failureMechanismsCache = data)));
  }

  fetchAllMaintainableItems(forceRefresh: boolean = false): Observable<any> {
    if (this.allMaintainableItemsCache && !forceRefresh) {
      return of(this.allMaintainableItemsCache);
    }
    return this.http
      .get(`${this.apiUrl}/filter/getallmaintainableitems`)
      .pipe(tap((data) => (this.allMaintainableItemsCache = data)));
  }

  fetchUserFeaturePersmissions() {
    let appname = "badactor";
    return this.http.get(
      `${this.roleUrl}/user/get-users-permissions-features?applicationname=${appname}`
    );
  }

  uploadCSVData(formData: FormData) {
    return this.http.post(`${this.apiUrl}/ClassifyData/import-classification-data`, formData);
  }

  fetchAllPerformedWorkAI(forceRefresh: boolean = false): Observable<any> {
    if (this.allPerformedWorkAICache && !forceRefresh) {
      return of(this.allPerformedWorkAICache);
    }
    return this.http
      .get(`${this.apiUrl}/Filter/getallperformedworks`)
      .pipe(tap((data) => (this.allPerformedWorkAICache = data)));
  }
}
